<template>
  <div class="home">
    <div class="top">
      <div>
        <RexButtonV3
          @onClick="display"
          variant="default"
          :styling="stylingAddWidget"
          id="btn-add-widget"
        >
          {{ $t('addWidget') }}
        </RexButtonV3>
      </div>
    </div>

    <RexModalV3
      :show="showModal"
      @onClose="showModal = false"
      :title="$t('addWidget')"
      :enableFooter="true"
    >
      <template #body>
        <div class="widgetFormBody m-body d-flex flex-column mb-3">
          <div class="d-flex flex-column">
            <div class="mb-lg-2 mb-xl-2">
              <RexModalLabel
                :primaryLabel="$t('name')"
                :secondaryLabel="$t('required')"
              />
            </div>
            <div>
              <RexModalInput
                type="text"
                placeholder
                style="width: 31rem; height: 2.5em"
                v-model="widgetName"
                id="input-widget-name"
              />
              <RexInfoLabel
                :isError="true"
                customStyle="width:31em; marginBottom: 0"
                v-if="valid.name"
                :info="$t('widgetNameValidation')"
              />
            </div>
          </div>
          <div class="d-flex flex-column mt-4">
            <div class="mb-lg-2 mb-xl-2">
              <RexModalLabel
                :primaryLabel="$t('capability')"
                :secondaryLabel="$t('required')"
              />
            </div>
            <div>
              <RexSearchSelect
                :data="capabilityList"
                :current="capabilitySelected"
                :objectValueIndex="1"
                width="31rem"
                @onChoose="selectCapability"
                :isSearch="true"
                id="search-select-add-widget-capability"
              />
              <RexInfoLabel
                :isError="true"
                customStyle="width:31em; marginBottom: 0"
                v-if="valid.capability"
                :info="$t('capabilityValidation')"
              />
            </div>
          </div>
          <div
            class="d-flex flex-column mt-4"
            v-if="
              !(
                isLogCapability ||
                isAnalyticsCapability ||
                isDatabaseCapability
              ) || !isRedirectToAddWidget
            "
          >
            <div class="mb-lg-2 mb-xl-2">
              <RexModalLabel
                :primaryLabel="$t('application')"
                :secondaryLabel="$t('required')"
              />
            </div>
            <div>
              <RexSearchSelect
                :data="servicesList"
                :current="serviceSelected"
                :objectValueIndex="1"
                width="31rem"
                @onChoose="selectService"
                :isSearch="true"
                id="search-select-add-widget-application"
              />
              <RexInfoLabel
                :isError="true"
                customStyle="width:31em; marginBottom: 0"
                v-if="valid.application"
                :info="$t('applicationValidation')"
              />
            </div>
          </div>
          <div
            class="d-flex flex-column mt-4 mb-4"
            v-if="
              !(
                isLogCapability ||
                isAnalyticsCapability ||
                isDatabaseCapability
              ) || !isRedirectToAddWidget
            "
          >
            <div class="mb-lg-2 mb-xl-2">
              <RexModalLabel
                :primaryLabel="$t('metric')"
                :secondaryLabel="$t('required')"
              />
            </div>
            <div>
              <RexSearchSelect
                :data="metricList"
                :current="metricSelected"
                :objectValueIndex="1"
                width="31rem"
                @onChoose="selectMetric"
                :isSearch="true"
                id="search-select-add-widget-metrics"
              />
              <RexInfoLabel
                :isError="true"
                customStyle="width:31em; marginBottom: 0"
                v-if="valid.metricsName"
                :info="$t('metricsValidation')"
              />
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="cus-add-wid-footer-box">
          <RexButtonV3
            variant="default"
            @onClick="clear"
            id="btn-clr-widget"
            class="mr-4"
          >
            {{ $t('clear') }}
          </RexButtonV3>

          <RexButtonV3
            variant="primary"
            @onClick="submitForm"
            id="btn-submit-widget"
            :style="isDatabaseCapability ? { width: '12rem' } : {}"
          >
            {{ getSubmitBtnText }}
          </RexButtonV3>
        </div>
      </template>
    </RexModalV3>
  </div>
</template>

<script>
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import RexModalSelect from '@/rex-components/rex-modal-select.vue';
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexModalInput from '@/rex-components/rex-modal-input.vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import {
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_MOBILE,
  CAPABILITY_VM,
  CAPABILITY_A,
  CAPABILITY_B,
  CAPABILITY_M,
} from '@/utils/constants';
import { MetricType, Capability, ScopeType } from '@/utils/enums/metricType';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  apmMetric,
  browserMetric,
  mobileMetric,
  vmMetrics,
} from '@/utils/custom/metricList';
import {
  APPLICATION_SUMMARY,
  VM_SUMMARY,
} from '@/components/Home/utils/custom.home.types';
import templates, {
  getSummaryWidget,
} from '@/utils/custom/custom.graphs.config';
import BuildConfig from '@/environment';
import { permissionTypes } from '@/router/constant';
// @ is an alias to /src

export default {
  name: 'AddWidgetModal',
  emits: ['widget'],
  components: {
    RexButtonV3,
    RexModalV3,
    RexModalSelect,
    RexModalLabel,
    RexModalInput,
    RexInfoLabel,
    RexSearchSelect,
  },
  data() {
    return {
      stylingAddWidget: {
        height: '2.5rem',
        width: '7.375rem',
      },
      text: '',
      showModal: false,
      metricSelected: {},
      searchCapability: this.$t('searchCapability'),
      serviceSelected: {},
      widgetName: '',
      capabilitySelected: {},
      capabilityList: [
        {
          key: Capability.APM,
          label: this.$t('apm'),
        },
        {
          key: Capability.BROWSER,
          label: this.$t('browserMonitoring'),
        },
        {
          key: Capability.MOBILE,
          label: this.$t('mobileMonitoring'),
        },
        {
          key: Capability.VM,
          label: this.$t('vmCapability'),
        },
        {
          key: Capability.LOG,
          label: this.$t('logMonitoring'),
        },
        {
          key: Capability.ANALYTICS,
          label: this.$t('analyticsCap'),
        },
        {
          key: Capability.DATABASE,
          label: this.$t('databaseMonitoring'),
        },
      ],
      valid: {
        name: false,
        capability: false,
        application: false,
        metricsName: false,
      },
      invokeClear: false,
      servicesList: [],
      metricList: [],
    };
  },
  computed: {
    ...mapState('apmState/apmCommonState', ['services', 'currentService']),
    ...mapState('browserState/browserCommonState', [
      'currentBrowserService',
      'browserServices',
    ]),
    ...mapGetters('browserState/browserCommonState', [
      'getAllBrowserServices',
      'getCurrentBrowserService',
    ]),
    ...mapGetters('apmState/apmCommonState', ['getServices']),
    ...mapGetters('mobileState/mobileCommonState', ['getAllMobileServices']),
    ...mapGetters('vmState/vmCommonState', ['getAllVMServices']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    isRedirectToAddWidget() {
      return (
        this.capabilitySelected.key === Capability.LOG ||
        this.capabilitySelected.key === Capability.ANALYTICS ||
        this.capabilitySelected.key === Capability.DATABASE
      );
    },
    getSubmitBtnText() {
      if (this.capabilitySelected.key === Capability.LOG)
        return this.$t('goToLM');
      if (this.capabilitySelected.key === Capability.ANALYTICS)
        return this.$t('goToAnalytics');
      if (this.capabilitySelected.key === Capability.DATABASE) {
        return this.$t('goToCustomDb');
      }
      return this.$t('add');
    },
    isLogCapability() {
      return this.capabilitySelected.key === Capability.LOG;
    },
    isAnalyticsCapability() {
      return this.capabilitySelected.key === Capability.ANALYTICS;
    },
    isDatabaseCapability() {
      return this.capabilitySelected.key === 'Database';
    },
  },
  methods: {
    ...mapActions('apmState/apmCommonState', {
      getServicesAction: 'getServices',
    }),
    ...mapActions('browserState/browserCommonState', ['getBrowserData']),
    ...mapActions('mobileState/mobileCommonState', ['GET_MOBILE_SERVICES']),
    ...mapActions('vmState/vmCommonState', ['getVMData']),
    checkCapabilityList() {
      const featureFlag =
        BuildConfig.VUE_APP_ACTIVE_CAPABILITIES ||
        process.env.VUE_APP_ACTIVE_CAPABILITIES ||
        '';
      const activeCapabilities = featureFlag.split(',');
      if (activeCapabilities.length > 0 && !activeCapabilities.includes('')) {
        this.capabilityList = this.capabilityList.filter((capability) => {
          const capabilityKey = capability.key.toLowerCase();
          return activeCapabilities.includes(capabilityKey);
        });
      }
    },
    async loadBrowserServices() {
      try {
        await this.getBrowserData({
          duration: this.getDurationTime,
          serviceReq: { capability: CAPABILITY_B },
          permissions: [permissionTypes.WRITE],
        });
      } catch (error) {
        console.log('error', error);
      }
    },
    async loadAPMServices() {
      try {
        await this.getServicesAction({
          duration: this.getDurationTime,
          serviceReq: { capability: CAPABILITY_A },
          permissions: [permissionTypes.WRITE],
        });
      } catch (error) {
        console.log('error', error);
      }
    },
    async loadMobileService() {
      try {
        await this.GET_MOBILE_SERVICES({
          duration: this.getDurationTime,
          serviceReq: { capability: CAPABILITY_M },
          permissions: [permissionTypes.WRITE],
        });
      } catch (error) {
        console.log('error', error);
      }
    },
    async loadVMServices() {
      try {
        await this.getVMData({
          duration: this.getDurationTime,
          keyword: '',
          serviceReq: {
            capability: 'V',
          },
          permissions: [permissionTypes.WRITE],
        });
      } catch (error) {
        console.log('error', error);
      }
    },
    async serviceTypeHandler(key) {
      let services = [];
      let metrics = [];

      switch (key) {
        case 'Browser':
        case CAPABILITY_BROWSER:
          await this.loadBrowserServices();
          services = this.getAllBrowserServices;
          metrics = browserMetric;
          break;
        case CAPABILITY_APM:
          await this.loadAPMServices();
          metrics = apmMetric;
          services = this.getServices;
          break;
        case CAPABILITY_VM:
          await this.loadVMServices();
          metrics = vmMetrics;
          services = this.getAllVMServices;
          break;
        case 'Mobile':
        case CAPABILITY_MOBILE:
          await this.loadMobileService();
          metrics = mobileMetric;
          services = this.getAllMobileServices;
          break;
        default:
          break;
      }
      this.servicesList = services;
      this.metricList = metrics;
    },
    clicked() {
      this.showModal = false;
    },
    display() {
      this.clear();
      this.showModal = true;
    },
    hide(evt) {
      if (evt.trigger === 'backdrop') {
        evt.preventDefault();
        this.showModal = false;
      }
    },
    selectCapability(item) {
      this.capabilitySelected = item;
      this.serviceTypeHandler(item.key);
      this.serviceSelected = '';
    },
    selectService(item) {
      this.serviceSelected = item;
    },
    selectMetric(option) {
      this.metricSelected = option;
    },
    formValid() {
      this.clearValidations();
      if (
        this.widgetName === '' ||
        this.widgetName.length > 25 ||
        !/^( )?[a-zA-Z0-9]+( [a-zA-Z0-9]+)*( )?$/.test(this.widgetName)
      )
        this.valid.name = true;
      if (!this.capabilitySelected.key) this.valid.capability = true;
      if (!this.metricSelected.key) this.valid.metricsName = true;
      if (!this.serviceSelected.key) this.valid.application = true;
    },
    clearValidations() {
      (this.valid.name = false),
        (this.valid.capability = false),
        (this.valid.application = false),
        (this.valid.metricsName = false);
    },
    clear() {
      (this.widgetName = ''),
        (this.capabilitySelected = {}),
        (this.serviceSelected = {}),
        (this.metricSelected = {});
      this.servicesList = [];
      this.metricList = [];
      this.clearValidations();
    },
    getSummaryWidgetData() {
      return getSummaryWidget({
        widgetName: this.widgetName,
        serviceKey: this.serviceSelected.key,
        serviceName: this.serviceSelected.label,
        capability: this.capabilitySelected.key?.toUpperCase(),
      });
    },
    handleRedirection() {
      if (this.capabilitySelected.key === Capability.DATABASE) {
        this.$router.push({
          name: 'customQueryOverview',
        });
      } else
        this.$router.push({
          name:
            this.capabilitySelected.key === Capability.ANALYTICS
              ? 'analytics'
              : 'log',
          query: {
            currView: 'searches',
          },
        });
    },
    async submitForm() {
      try {
        if (this.isRedirectToAddWidget) {
          this.handleRedirection();
          return;
        }
        this.formValid();
        if (
          !this.valid.name &&
          !this.valid.capability &&
          !this.valid.application &&
          !this.valid.metricsName
        ) {
          let key = this.metricSelected.key;
          let clone;
          if (key === APPLICATION_SUMMARY || key === VM_SUMMARY) {
            clone = this.getSummaryWidgetData();
          } else {
            let filteredObj = templates.lineCharts.filter(
              (item) => item.id === key,
            );
            let temp = filteredObj[0];
            clone = JSON.parse(JSON.stringify(temp));
            clone.serviceName = this.serviceSelected.label;
            clone.serviceKey = this.serviceSelected.key;
            clone.widgetName = this.widgetName;
            clone.id = Date.now();
          }
          this.$emit('widget', clone);
          this.showModal = false;
          this.clear();
        }
      } catch (error) {
        console.log('error', error);
      }
    },
  },
  mounted() {
    this.checkCapabilityList();
  },
  watch: {
    widgetName() {
      if (this.widgetName.length > 25 || !this.widgetName) {
        this.valid.name = true;
      } else this.valid.name = false;
    },
  },
};
</script>
<style lang="scss">
.widgetFormBody {
  height: 33rem;
}
.modalRow {
  padding-top: 2.5rem;
  padding-left: 2.5rem;
  color: $font-primary;
  width: 32.18rem;
  text-align: left;
}
.modalCol {
  padding-left: 1.25rem;
  font-family: $semi-bold !important;
}
.modalOption {
  padding-top: 0.9375rem;
  max-width: 18.75rem;
  font-family: $regular !important;
}
.modalSub2 {
  padding-top: 0.75rem;
  font-size: 0.75rem;

  font-family: $regular !important;
  max-width: 18.75rem;
}
.modalSub {
  padding-top: 0rem;
  font-size: 0.75rem;
  font-family: $regular !important;
  max-width: 24.75rem;
}
.modalFootRow {
  padding-left: 1.25rem;
  font-family: $regular !important;
}
.modalFootCol {
  padding-left: 0.625rem;
}
.validation {
  color: $poor;
  font-family: $regular !important;
}
.cus-add-wid-footer-box {
  display: flex;
  justify-content: flex-end;
}
@media screen and (max-width: 1199px) {
  .m-body {
    font-size: 12px;
  }
}

@media screen and (min-width: 1200px) {
  .m-body {
    font-size: 14px;
  }
}
@media screen and (min-width: 1600px) {
  .m-body {
    font-size: 16px;
  }
}
@media screen and (min-width: 1900px) {
  .m-body {
    font-size: 18px;
  }
}
</style>
